export type PriceChangeLine = {
  recipe_id: string;
  code: string;
  name: string;
  kind: string;
  group_name: string;
  roof_style: string;
  current_wholesale_usd: number | null;
  current_msrp_usd: number | null;
  suggested_wholesale_usd: number | null;
  suggested_msrp_usd: number | null;
  new_wholesale_usd: number | null;
  new_msrp_usd: number | null;
  new_wholesale_confirmed: boolean;
  new_msrp_confirmed: boolean;
};

export type PriceChangeStatus = "draft" | "committed";

/** Empty keeps every row. Blank shows only empty amounts. Filled shows only amounts that are set. */
export type PriceChangePricePresence = "" | "blank" | "filled";

export type PriceChangeNarrowFilters = {
  kind: string;
  newWholesale: PriceChangePricePresence;
  newMsrp: PriceChangePricePresence;
  currentWholesale: PriceChangePricePresence;
  currentMsrp: PriceChangePricePresence;
  cogsWholesale: PriceChangePricePresence;
  cogsMsrp: PriceChangePricePresence;
};

export const EMPTY_PRICE_CHANGE_NARROW: PriceChangeNarrowFilters = {
  kind: "",
  newWholesale: "",
  newMsrp: "",
  currentWholesale: "",
  currentMsrp: "",
  cogsWholesale: "",
  cogsMsrp: "",
};

const MAX_LINES = 4000;

export function parsePriceChangeMoney(
  raw: unknown,
): { ok: true; value: number | null } | { ok: false; error: string } {
  if (raw === null || raw === undefined) return { ok: true, value: null };
  if (typeof raw === "number") {
    if (!Number.isFinite(raw) || raw < 0) {
      return { ok: false, error: "Price must be a non-negative number." };
    }
    return { ok: true, value: Math.round(raw * 100) / 100 };
  }
  const text = String(raw).trim();
  if (!text) return { ok: true, value: null };
  const n = Number(text.replace(/[$,]/g, ""));
  if (!Number.isFinite(n) || n < 0) {
    return { ok: false, error: "Price must be a dollar amount or blank." };
  }
  return { ok: true, value: Math.round(n * 100) / 100 };
}

function matchesPresence(value: number | null, presence: PriceChangePricePresence | undefined): boolean {
  if (!presence) return true;
  return presence === "blank" ? value == null : value != null;
}

export function filterPriceChangeLines(
  lines: readonly PriceChangeLine[],
  filters: {
    search?: string;
    group?: string;
    roof?: string;
    kind?: string;
    newWholesale?: PriceChangePricePresence;
    newMsrp?: PriceChangePricePresence;
    currentWholesale?: PriceChangePricePresence;
    currentMsrp?: PriceChangePricePresence;
    cogsWholesale?: PriceChangePricePresence;
    cogsMsrp?: PriceChangePricePresence;
  },
): PriceChangeLine[] {
  const search = (filters.search ?? "").trim().toLowerCase();
  const group = (filters.group ?? "").trim().toLowerCase();
  const roof = (filters.roof ?? "").trim().toLowerCase();
  const kind = (filters.kind ?? "").trim().toLowerCase();
  return lines.filter((line) => {
    if (group && line.group_name.trim().toLowerCase() !== group) return false;
    if (roof && line.roof_style.trim().toLowerCase() !== roof) return false;
    if (kind && line.kind.trim().toLowerCase() !== kind) return false;
    if (!matchesPresence(line.new_wholesale_usd, filters.newWholesale)) return false;
    if (!matchesPresence(line.new_msrp_usd, filters.newMsrp)) return false;
    if (!matchesPresence(line.current_wholesale_usd, filters.currentWholesale)) return false;
    if (!matchesPresence(line.current_msrp_usd, filters.currentMsrp)) return false;
    if (!matchesPresence(line.suggested_wholesale_usd, filters.cogsWholesale)) return false;
    if (!matchesPresence(line.suggested_msrp_usd, filters.cogsMsrp)) return false;
    if (!search) return true;
    const hay = `${line.code} ${line.name} ${line.kind} ${line.group_name} ${line.roof_style}`.toLowerCase();
    return hay.includes(search);
  });
}

export function priceChangeFacetLabels(
  lines: readonly PriceChangeLine[],
  key: "group_name" | "roof_style" | "kind",
): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const line of lines) {
    const label = line[key].trim();
    if (!label) continue;
    const fold = label.toLowerCase();
    if (seen.has(fold)) continue;
    seen.add(fold);
    out.push(label);
  }
  return out.sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" }));
}

export function countPriceChangeEdits(lines: readonly PriceChangeLine[]): number {
  return lines.filter((line) => line.new_wholesale_usd != null || line.new_msrp_usd != null).length;
}

export function countPriceChangeConfirmations(lines: readonly PriceChangeLine[]): { confirmed: number; total: number } {
  let confirmed = 0;
  let total = 0;
  for (const line of lines) {
    if (line.new_wholesale_usd != null) {
      total += 1;
      if (line.new_wholesale_confirmed === true) confirmed += 1;
    }
    if (line.new_msrp_usd != null) {
      total += 1;
      if (line.new_msrp_confirmed === true) confirmed += 1;
    }
  }
  return { confirmed, total };
}

function readConfirmed(value: unknown): boolean {
  if (value === true) return true;
  if (typeof value === "number") return value === 1;
  if (typeof value !== "string") return false;
  const text = value.trim().toLowerCase();
  return text === "yes" || text === "y" || text === "true" || text === "1" || text === "x";
}

function asText(value: unknown): string {
  return typeof value === "string" ? value.trim() : "";
}

export function normalizePriceChangeLines(
  raw: unknown,
): { ok: true; lines: PriceChangeLine[] } | { ok: false; error: string } {
  if (!Array.isArray(raw)) return { ok: false, error: "lines must be a list." };
  if (raw.length > MAX_LINES) return { ok: false, error: `A program can hold ${MAX_LINES} items.` };
  const lines: PriceChangeLine[] = [];
  for (const item of raw) {
    if (!item || typeof item !== "object") return { ok: false, error: "Each line must be an object." };
    const row = item as Record<string, unknown>;
    const recipe_id = asText(row.recipe_id);
    const code = asText(row.code);
    if (!recipe_id || !code) return { ok: false, error: "Each line needs a recipe id and code." };
    const moneyKeys = [
      "current_wholesale_usd",
      "current_msrp_usd",
      "suggested_wholesale_usd",
      "suggested_msrp_usd",
      "new_wholesale_usd",
      "new_msrp_usd",
    ] as const;
    const money: Record<(typeof moneyKeys)[number], number | null> = {
      current_wholesale_usd: null,
      current_msrp_usd: null,
      suggested_wholesale_usd: null,
      suggested_msrp_usd: null,
      new_wholesale_usd: null,
      new_msrp_usd: null,
    };
    for (const key of moneyKeys) {
      const parsed = parsePriceChangeMoney(row[key]);
      if (!parsed.ok) return { ok: false, error: `${code}: ${parsed.error}` };
      money[key] = parsed.value;
    }
    lines.push({
      recipe_id,
      code,
      name: asText(row.name),
      kind: asText(row.kind),
      group_name: asText(row.group_name),
      roof_style: asText(row.roof_style),
      ...money,
      new_wholesale_confirmed: money.new_wholesale_usd != null && readConfirmed(row.new_wholesale_confirmed),
      new_msrp_confirmed: money.new_msrp_usd != null && readConfirmed(row.new_msrp_confirmed),
    });
  }
  return { ok: true, lines };
}
